// The customer's own documents for their upcoming appointment (AB#774), reached from the manage link in
// the confirmation or reminder: api/ams/booking-documents on the booking-session slice (publicApi), never
// the staff client in lib/api.ts, which signs with MSAL. See AmsPublicBookingDocumentsController.
import { publicApi } from "@/services/publicApi"
import { getBookingSessionToken, clearBookingSessionToken } from "@/auth/getBookingSessionToken"
import { NOT_FOUND_MESSAGE, LOCKED_OUT_MESSAGE } from "@/app/booking-cancel/manageLinkCopy"
import { formatFileSize } from "@/app/booking-calendar/api/documentsApi"

// Build-time replaced by Bun's define plugin; in dev (HMR) this is undefined
declare const __API_BASE_URL__: string | undefined
const API_BASE_URL = typeof __API_BASE_URL__ !== "undefined" ? __API_BASE_URL__ : ""

export const PUBLIC_DOCUMENTS_PATH = "/api/ams/booking-documents"

/** PublicBookingDocumentResponse: one document as the customer may see it. No uploader, no scan status. */
export interface PublicBookingDocument {
  id: string
  fileName: string
  sizeBytes: number
  uploadedAt: string
  /** Their own upload, cleared by the malware scan. */
  canDownload: boolean
  /** Their own upload, in any scan status. */
  canRemove: boolean
  /** Still waiting for the malware scan. */
  pending: boolean
}

/** PublicBookingDocumentsAccessResponse: the granted appointment and its documents, newest first. */
export interface PublicBookingDocuments {
  siteName: string | null
  start: string
  end: string
  /** When the grant lapses and the confirmation number must be entered again. */
  expiresAt: string
  documents: PublicBookingDocument[]
  /**
   * The largest file the customer may upload, in bytes (BookingDocuments:MaxSizeBytes, capped at the
   * request cap). Optional so a server that predates it still works: the page falls back to MAX_UPLOAD_BYTES.
   */
  maxSizeBytes?: number
}

/** PublicBookingDocumentsAccessRequest. `approvalId` only when the phone challenge is on. */
export interface PublicBookingDocumentsAccessRequest {
  token: string
  confirmationCode: string
  approvalId?: string
}

/** PublicBookingDocumentErrorCodes. */
export type PublicBookingDocumentErrorCode =
  | "sessionRequired"
  | "noGrant"
  | "notUpcoming"
  | "approvalRequired"
  | "missingFile"
  | "disallowedType"
  | "tooMany"
  | "notBooked"
  | "tooLarge"
  | "storageError"
  | "notReady"

/**
 * The largest upload the server reads at all (AmsPublicBookingDocumentsController.MaxUploadRequestBytes).
 * A bigger file is refused here, before it is sent; the configured cap below it is still the server's.
 */
export const MAX_UPLOAD_BYTES = 12 * 1024 * 1024

/**
 * The largest file this customer may upload: the limit the server returned with their documents, never
 * above MAX_UPLOAD_BYTES; MAX_UPLOAD_BYTES itself when the server sent none.
 */
export function uploadLimitBytes(granted: Pick<PublicBookingDocuments, "maxSizeBytes"> | null | undefined): number {
  const limit = granted?.maxSizeBytes
  return typeof limit === "number" && Number.isFinite(limit) && limit > 0 ? Math.min(limit, MAX_UPLOAD_BYTES) : MAX_UPLOAD_BYTES
}

/** The sentence that states the upload limit, as the server's 413 answer says it ("Files must be 10 MB or smaller."). */
export function sizeLimitSentence(maxBytes: number): string {
  return `Files must be ${formatFileSize(maxBytes)} or smaller.`
}

const taggedApi = publicApi.enhanceEndpoints({ addTagTypes: ["PublicBookingDocuments"] })
const TAG = { type: "PublicBookingDocuments" as const, id: "granted" }

export const publicDocumentsApi = taggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * The granted appointment's documents. Also how the pages learn whether customer documents are on
     * at all: there is no public flag endpoint, and with the flags off this answers a bare 404, while
     * with them on and no grant it answers 404 with `{ code: "noGrant" }` (see documentsAvailability).
     */
    getPublicBookingDocuments: builder.query<PublicBookingDocuments, void>({
      query: () => PUBLIC_DOCUMENTS_PATH,
      providesTags: [TAG],
    }),
    /**
     * Earns this booking session a grant from the manage link's token and the confirmation code (and,
     * with the phone challenge on, an approved ViewBooking approval). Its answer is the list's answer,
     * so it goes straight into the list's cache rather than costing another (audited) read.
     */
    accessPublicBookingDocuments: builder.mutation<PublicBookingDocuments, PublicBookingDocumentsAccessRequest>({
      query: (body) => ({ url: `${PUBLIC_DOCUMENTS_PATH}/access`, method: "POST", body }),
      async onQueryStarted(_request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(publicDocumentsApi.util.upsertQueryData("getPublicBookingDocuments", undefined, data))
        } catch {
          // The page reads the error from the mutation itself.
        }
      },
    }),
    /** One file, as multipart field `file`. The list refetches either way: a refusal may mean the grant lapsed. */
    uploadPublicBookingDocument: builder.mutation<PublicBookingDocument, File>({
      query: (file) => {
        const form = new FormData()
        form.append("file", file, file.name)
        return { url: PUBLIC_DOCUMENTS_PATH, method: "POST", body: form }
      },
      invalidatesTags: [TAG],
    }),
    /** Removes one of the customer's own documents. */
    deletePublicBookingDocument: builder.mutation<void, string>({
      query: (documentId) => ({ url: `${PUBLIC_DOCUMENTS_PATH}/${encodeURIComponent(documentId)}`, method: "DELETE" }),
      invalidatesTags: [TAG],
    }),
  }),
})

export const {
  useGetPublicBookingDocumentsQuery,
  useAccessPublicBookingDocumentsMutation,
  useUploadPublicBookingDocumentMutation,
  useDeletePublicBookingDocumentMutation,
} = publicDocumentsApi

/** The HTTP status an RTK Query (or download) error carries, if any. */
export function failureStatus(error: unknown): number | null {
  const status = (error as { status?: unknown } | null | undefined)?.status
  return typeof status === "number" ? status : null
}

/** The `code` of a PublicBookingDocumentError body, if the error carried one. */
export function failureCode(error: unknown): string | null {
  const code = (error as { data?: { code?: unknown } } | null | undefined)?.data?.code
  return typeof code === "string" ? code : null
}

/** Whether the server said this session has no grant (never earned, or lapsed). */
export function isNoGrant(error: unknown): boolean {
  return failureStatus(error) === 404 && failureCode(error) === "noGrant"
}

/** "checking" until the first answer, then whether customer documents are on. */
export type DocumentsAvailability = "checking" | "on" | "off"

/**
 * Whether customer documents are on, from the list's answer. On: a list, or one of the coded 404s that
 * only exist with the flags on (`noGrant`, `notUpcoming`). Off: the bare 404 the flags-off endpoints
 * give, and anything else that leaves it unknown (a 429 or a 5xx), so the landing button stays disabled
 * and the documents page keeps its what-to-bring list rather than a form that cannot work.
 */
export function documentsAvailability(query: { data?: unknown; error?: unknown }): DocumentsAvailability {
  if (query.data !== undefined) return "on"
  if (query.error === undefined) return "checking"
  const code = failureCode(query.error)
  return failureStatus(query.error) === 404 && (code === "noGrant" || code === "notUpcoming") ? "on" : "off"
}

// The confirmation-code copy is the cancellation link's page's, so the two read the same.
export { NOT_FOUND_MESSAGE, LOCKED_OUT_MESSAGE }
export const EXPIRED_LINK_MESSAGE =
  "This link is no longer valid. Please use the link in your most recent appointment text or email, or call us for help."
export const NOT_UPCOMING_MESSAGE =
  "Documents can only be sent for an upcoming appointment. This appointment has already started, or it was cancelled."
export const SESSION_REQUIRED_MESSAGE = "Please open this page from the link in your appointment text or email."
export const GRANT_EXPIRED_MESSAGE =
  "For your security, please enter your confirmation number again to see your documents."
export const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again."
export const RATE_LIMITED_MESSAGE = "Too many requests. Please wait a minute and try again."

/** What the code form says when POST …/access is refused. 403 approvalRequired is handled by the page, not here. */
export function accessErrorMessage(error: unknown): string {
  const status = failureStatus(error)
  const code = failureCode(error)
  if (status === 404) return code === "notUpcoming" ? NOT_UPCOMING_MESSAGE : NOT_FOUND_MESSAGE
  if (status === 410) return EXPIRED_LINK_MESSAGE
  if (status === 429) return LOCKED_OUT_MESSAGE
  if (status === 400 && code === "sessionRequired") return SESSION_REQUIRED_MESSAGE
  return GENERIC_ERROR_MESSAGE
}

/**
 * What a customer is told when an upload is refused, by the server's code first, then the status. A file
 * too large names the limit, `maxBytes` (the page's uploadLimitBytes).
 */
export function uploadErrorMessage(error: unknown, maxBytes: number = MAX_UPLOAD_BYTES): string {
  const tooLarge = `This file is too large. ${sizeLimitSentence(maxBytes)}`
  switch (failureCode(error)) {
    case "disallowedType":
      return "This type of file can't be uploaded. Please choose a PDF or a plain-text (.txt) file."
    case "tooLarge":
      return tooLarge
    case "tooMany":
      return "Your appointment already has as many documents as it can hold. Remove one before adding another."
    case "notBooked":
      return "Documents can only be added to a booked appointment."
    case "missingFile":
      return "Please choose a file to upload."
    case "notUpcoming":
      return NOT_UPCOMING_MESSAGE
    case "storageError":
      return "The file could not be saved. Please try again."
  }
  switch (failureStatus(error)) {
    case 413:
      return tooLarge
    case 429:
      return RATE_LIMITED_MESSAGE
    default:
      return "The file could not be uploaded. Please try again."
  }
}

/** What a customer is told when a download is refused. */
export function downloadErrorMessage(error: unknown): string {
  switch (failureStatus(error)) {
    case 409:
      return "This document is still being checked, so it can't be downloaded yet."
    case 404:
      return "This document is no longer available."
    case 429:
      return RATE_LIMITED_MESSAGE
    default:
      return "The document could not be downloaded. Please try again."
  }
}

/** What a customer is told when a removal is refused. */
export function removeErrorMessage(error: unknown): string {
  if (failureStatus(error) === 429) return RATE_LIMITED_MESSAGE
  if (failureStatus(error) === 404) return "This document is no longer available."
  return "The document could not be removed. Please try again."
}

/** A download failure, shaped like an RTK Query error so the helpers above read both. */
export class DocumentDownloadError extends Error {
  constructor(
    readonly status: number,
    readonly data: unknown,
  ) {
    super(`Download failed: ${status}`)
  }
}

async function fetchContent(path: string): Promise<Response> {
  const token = await getBookingSessionToken()
  return fetch(`${API_BASE_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } })
}

/**
 * Streams one of the customer's own documents to a file save, as lib/api.ts's download does, but signed
 * with the booking-session token (the session holds the grant), retried once on a 401 exactly as
 * publicApi does. Throws a DocumentDownloadError for a refusal.
 */
export async function downloadPublicBookingDocument(document: Pick<PublicBookingDocument, "id" | "fileName">): Promise<void> {
  const path = `${PUBLIC_DOCUMENTS_PATH}/${encodeURIComponent(document.id)}/content`
  let response = await fetchContent(path)
  if (response.status === 401) {
    clearBookingSessionToken()
    response = await fetchContent(path)
  }
  if (!response.ok) {
    let data: unknown = null
    try {
      data = await response.json()
    } catch {
      // No JSON body: the status says enough.
    }
    throw new DocumentDownloadError(response.status, data)
  }

  const blob = await response.blob()
  const disposition = response.headers.get("Content-Disposition")
  const filename = disposition?.match(/filename="?([^";\n]+)"?/)?.[1] ?? document.fileName
  const url = URL.createObjectURL(blob)
  const a = window.document.createElement("a")
  a.href = url
  a.download = filename
  window.document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
